'use client';

import {type ReactNode, useCallback} from 'react';
// import clsx from 'clsx';
import {usePathname, useSearchParams} from 'next/navigation';

import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	// PaginationNext,
	// PaginationPrevious,
} from './shadcn-pagination';

export interface PaginationWithLinksProps {
	totalCount: number;
	pageSize: number;
	page: number;
	pageSearchParam?: string;
}

/**
 * Navigate with Nextjs links (need to update your own `pagination.tsx` to use Nextjs Link)
 *
 * @example
 * ```
 * <PaginationWithLinks
    page={1}
    pageSize={20}
    totalCount={500}
  />
 * ```
 */
export function PaginationWithLinks({
	pageSize,
	totalCount,
	page,
	pageSearchParam,
}: PaginationWithLinksProps) {
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const totalPageCount = Math.ceil(totalCount / pageSize);

	const buildLink = useCallback(
		(newPage: number) => {
			const key = pageSearchParam || 'page';
			if (!searchParams) {
				return `${pathname}?${key}=${newPage}`;
			}
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.set(key, String(newPage));
			return `${pathname}?${newSearchParams.toString()}`;
		},
		[searchParams, pathname, pageSearchParam],
	);

	const renderPageNumbers = () => {
		const items: ReactNode[] = [];
		const maxVisiblePages = 5;

		if (totalPageCount <= maxVisiblePages) {
			for (let i = 1; i <= totalPageCount; i++) {
				items.push(
					<PaginationItem key={i}>
						<PaginationLink
							href={buildLink(i)}
							isActive={page === i}
							title={
								page === i
									? 'Nåværende side'
									: `Gå til side ${i}`
							}
						>
							<span className="sr-only">Side </span>
							{i}
						</PaginationLink>
					</PaginationItem>,
				);
			}
		} else {
			items.push(
				<PaginationItem key={1}>
					<PaginationLink
						href={buildLink(1)}
						isActive={page === 1}
						title={page === 1 ? 'Nåværende side' : 'Gå til side 1'}
					>
						<span className="sr-only">Side </span>1
					</PaginationLink>
				</PaginationItem>,
			);

			if (page > 3) {
				items.push(
					<PaginationItem key="ellipsis-start">
						<PaginationEllipsis />
					</PaginationItem>,
				);
			}

			const start = Math.max(2, page - 1);
			const end = Math.min(totalPageCount - 1, page + 1);

			for (let i = start; i <= end; i++) {
				items.push(
					<PaginationItem key={i}>
						<PaginationLink
							href={buildLink(i)}
							isActive={page === i}
							title={
								page === i
									? 'Nåværende side'
									: `Gå til side ${i}`
							}
						>
							<span className="sr-only">Side </span>
							{i}
						</PaginationLink>
					</PaginationItem>,
				);
			}

			if (page < totalPageCount - 2) {
				items.push(
					<PaginationItem key="ellipsis-end">
						<PaginationEllipsis />
					</PaginationItem>,
				);
			}

			items.push(
				<PaginationItem key={totalPageCount}>
					<PaginationLink
						href={buildLink(totalPageCount)}
						isActive={page === totalPageCount}
						title={
							page === totalPageCount
								? 'Nåværende side'
								: `Gå til side ${totalPageCount}`
						}
					>
						<span className="sr-only">Side </span>
						{totalPageCount}
					</PaginationLink>
				</PaginationItem>,
			);
		}

		return items;
	};

	return (
		<Pagination aria-label="Sider" className="justify-start">
			<PaginationContent>
				{/* <PaginationItem>
					<PaginationPrevious
						href={buildLink(Math.max(page - 1, 1))}
						aria-disabled={page === 1}
						tabIndex={page === 1 ? -1 : undefined}
						className={page === 1 ? 'pointer-events-none opacity-50' : undefined}
					/>
				</PaginationItem> */}
				{renderPageNumbers()}
				{/* <div className={clsx('flex', 'sm:hidden')}>
					<span>
						side {page} av {totalPageCount}
					</span>
				</div> */}
				{/* <PaginationItem>
					<PaginationNext
						href={buildLink(Math.min(page + 1, totalPageCount))}
						aria-disabled={page === totalPageCount}
						tabIndex={page === totalPageCount ? -1 : undefined}
						className={
							page === totalPageCount ? 'pointer-events-none opacity-50' : undefined
						}
					/>
				</PaginationItem> */}
			</PaginationContent>
		</Pagination>
	);
}
